/**
 * @module task_template.actions
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import { httpRequest } from '~/adapters/httpRequest';
import { host } from '~/config/settings/worktango.settings';
import { statuses } from '~/config/settings/notification.settings';
import { urls } from '~/tools/urls';
import * as languages from '../languages';
import * as schemas from '../schemas';
import * as transformers from '../transformers';

const fetchTaskTemplateList = createAsyncThunk(
  'action-planning/fetchTaskTemplateList',
  async (empty, { rejectWithValue }) => {
    const url = `${host}/action-planning/library`;
    const res = await httpRequest(url);

    if (res.error) return rejectWithValue(res.error);

    const templateList = transformers.taskTemplatesFrom(res.payload);
    const normalizedData = normalize(templateList, [schemas.taskTemplate]);

    return normalizedData;
  },
);

// REFACTOR: Handle pagination differently through the Redux store.
// Can this be combined with the regular `fetchTaskTemplateList()` action creator
// by providing optional params, rather than creating a specialized function?
const fetchPaginatedTaskTemplates = createAsyncThunk(
  'action-planning/fetchPaginatedTaskTemplate',
  async (
    // @ts-expect-error TS(2322): Type '{}' is not assignable to type 'ThunkArg'.
    { page, limit, categoryIds, status, effectiveness, title, order = 'desc', isUniversal = true } = {},
    { rejectWithValue },
  ) => {
    const queryParams = urls.queryStringify(
      {
        onlyUniversalLibraryActions: isUniversal,
        order,
        page,
        limit,
        categoryIds,
        status,
        effectiveness,
        title,
      },
      { arrayFormat: 'indices', encode: true },
    );
    const url = `${host}/action-planning/library?${queryParams}`;
    const res = await httpRequest(url);

    if (res.error) return rejectWithValue(res.error);

    const templateList = transformers.taskTemplatesFrom(res.payload);

    // REFACTOR: This data should be normalized and merged with the Redux state.
    // This is unstable state that is only visible to the components handling
    // these results. Very hard to follow!

    return {
      taskTemplates: templateList,
      pagination: res?.meta?.pagination,
    };
  },
);

const fetchTaskTemplate = createAsyncThunk(
  'action-planning/fetchTaskTemplate',
  // @ts-expect-error TS(2339): Property 'templateId' does not exist on type 'void... Remove this comment to see the full error message
  async ({ templateId = null }) => {
    const url = `${host}/action-planning/library/${templateId}`;
    const res = await httpRequest(url);
    const template = transformers.taskTemplateFrom(res.payload);

    const normalizedData = normalize(template, schemas.taskTemplate);

    return normalizedData;
  },
);

const createTaskTemplate = createAsyncThunk(
  'action-planning/createTaskTemplate',
  async (
    // @ts-expect-error TS(2339): Property 'companyId' does not exist on type 'void'... Remove this comment to see the full error message
    { companyId = null, translations = {}, categoryIds = [], status = null, effectiveness = null },
    { rejectWithValue },
  ) => {
    const url = `${host}/action-planning/library`;
    const res = await httpRequest(url, {
      method: 'POST',
      body: {
        companyId,
        translations,
        categoryIds,
        status,
        effectiveness,
      },
    });

    if (res.error) return rejectWithValue(res.error);

    const template = transformers.taskTemplateFrom(res.payload);
    const normalizedData = normalize(template, schemas.taskTemplate);

    return {
      ...normalizedData,
      notification: {
        title: languages.t('notifications.title.done'),
        message: languages.t('notifications.message.templateCreated'),
        status: statuses.success,
      },
    };
  },
);

const modifyTaskTemplate = createAsyncThunk(
  'action-planning/modifyTaskTemplate',
  async (
    {
      // @ts-expect-error TS(2339): Property 'templateId' does not exist on type 'void... Remove this comment to see the full error message
      templateId = null,
      // @ts-expect-error TS(2339): Property 'companyId' does not exist on type 'void'... Remove this comment to see the full error message
      companyId = null,
      // @ts-expect-error TS(2339): Property 'surveyTypeId' does not exist on type 'vo... Remove this comment to see the full error message
      surveyTypeId = null,
      // @ts-expect-error TS(2339): Property 'translations' does not exist on type 'vo... Remove this comment to see the full error message
      translations = {},
      // @ts-expect-error TS(2339): Property 'categoryIds' does not exist on type 'voi... Remove this comment to see the full error message
      categoryIds = null,
      // @ts-expect-error TS(2339): Property 'effectiveness' does not exist on type 'v... Remove this comment to see the full error message
      effectiveness = null,
      // @ts-expect-error TS(2339): Property 'status' does not exist on type 'void'.
      status = null,
    },
    { rejectWithValue },
  ) => {
    const url = `${host}/action-planning/library/${templateId}`;
    const res = await httpRequest(url, {
      method: 'PATCH',
      body: {
        companyId,
        surveyTypeId,
        translations,
        categoryIds,
        effectiveness,
        status,
      },
    });

    if (res.error) return rejectWithValue(res.error);

    const template = transformers.taskTemplateFrom(res.payload);
    const normalizedData = normalize(template, schemas.taskTemplate);

    return {
      ...normalizedData,
      notification: {
        title: languages.t('notifications.title.done'),
        message: languages.t('notifications.message.templateUpdated'),
        status: statuses.success,
      },
    };
  },
);

const removeTaskTemplate = createAsyncThunk(
  'action-planning/removeTaskTemplate',
  async (templateId, { rejectWithValue }) => {
    const url = `${host}/action-planning/library/${templateId}`;
    const res = await httpRequest(url, {
      method: 'DELETE',
    });

    if (res.error) return rejectWithValue(res.error);

    return {
      id: templateId,
      notification: {
        title: languages.t('notifications.title.done'),
        message: languages.t('notifications.message.templateRemoved'),
        status: statuses.success,
      },
    };
  },
);

export const taskTemplateActions = {
  fetchTaskTemplateList,
  fetchPaginatedTaskTemplates,
  fetchTaskTemplate,
  createTaskTemplate,
  modifyTaskTemplate,
  removeTaskTemplate,
};
